import type { ReactNode } from 'react';
import { replaceAny } from 'replace-any';

import { PLURAL_PLACEHOLDER_NAME, REGEXP_PLACEHOLDER, REGEXP_WRAPPER } from './constants';
import { lookup } from './lookup';
import { interpolateAll } from './string';
import type { PhraseMap, PlaceholderAndWrapperMap, PlaceholderMap, WrapperKeyMap, WrapperMap } from './types';

const splitPlaceholdersAndWrappers = (placeholdersAndWrappers: PlaceholderAndWrapperMap) => {
  const placeholders: PlaceholderMap = {};
  const wrapperFunctions: WrapperMap = {};
  for (const [name, value] of Object.entries(placeholdersAndWrappers)) {
    if (typeof value === 'function') {
      wrapperFunctions[name] = value;
    } else {
      placeholders[name] = value;
    }
  }

  return { placeholders, wrapperFunctions };
};

const localizeNumericPlaceholders = (placeholders: PlaceholderMap, locale: string): Record<string, string> =>
  Object.keys(placeholders).reduce<Record<string, string>>((updatedPlaceholders, currentPlaceholder) => {
    const currentValue = placeholders[currentPlaceholder];

    if (typeof currentValue === 'number') {
      return {
        ...updatedPlaceholders,
        [currentPlaceholder]: currentValue.toLocaleString(locale),
      };
    }

    return {
      ...updatedPlaceholders,
      [currentPlaceholder]: currentValue,
    };
  }, {});

type Interpolate = (locale: string, phrases: PhraseMap, key: string, placeholders?: PlaceholderMap) => string;
export const interpolate: Interpolate = (locale, phrases, key, placeholders = {}) => {
  const str = lookup(locale, phrases, key, placeholders[PLURAL_PLACEHOLDER_NAME]);
  if (!str) {
    return '';
  }

  const placeholdersWithLocalizedNumbers = localizeNumericPlaceholders(placeholders, locale);

  return interpolateAll(REGEXP_PLACEHOLDER, str, placeholdersWithLocalizedNumbers);
};

type InterpolateWithReactWrappers = (
  locale: string,
  phrases: PhraseMap,
  key: string,
  placeholdersAndWrappers?: PlaceholderAndWrapperMap
) => ReactNode;
export const interpolateWithReactWrappers: InterpolateWithReactWrappers = (
  locale,
  phrases,
  key,
  placeholdersAndWrappers = {}
) => {
  const { placeholders, wrapperFunctions } = splitPlaceholdersAndWrappers(placeholdersAndWrappers);
  const str = interpolate(locale, phrases, key, placeholders);
  if (!str) {
    return '';
  }

  const wrappers = [
    {
      regexp: REGEXP_WRAPPER,
      replace: (_key: string, _match: string, wrapper: string, text: string): ReactNode => {
        const trimmedText = text.trim();
        const wrapperFn = wrapperFunctions[wrapper];

        if (!wrapperFn) {
          return trimmedText;
        }

        return wrapperFn(trimmedText);
      },
    },
  ];

  return replaceAny(wrappers, str);
};

export const getStringInterpolator = (
  usesWrappers: WrapperKeyMap,
  feature: string
): ((
  locale: string,
  phrases: PhraseMap
) => Record<string, (placeholdersAndWrappers: PlaceholderAndWrapperMap) => ReactNode | string>) & {
  readonly feature: string;
} => {
  const StringInterpolator = function (locale: string, phrases: PhraseMap) {
    const placeholderObj = {};

    return new Proxy(placeholderObj, {
      get(_target, prop: string) {
        return function (placeholdersAndWrappers: PlaceholderAndWrapperMap = {}) {
          if (usesWrappers[prop]) {
            return interpolateWithReactWrappers(locale, phrases, prop, placeholdersAndWrappers);
          }

          const { placeholders } = splitPlaceholdersAndWrappers(placeholdersAndWrappers);

          return interpolate(locale, phrases, prop, placeholders);
        };
      },
    });
  };
  StringInterpolator.feature = feature;

  return StringInterpolator as ((
    locale: string,
    phrases: PhraseMap
  ) => Record<string, (placeholdersAndWrappers: PlaceholderAndWrapperMap) => ReactNode | string>) & {
    readonly feature: string;
  };
};
